/**
 * KIOSK CONTROLLER (B6): idle → attract → wake → reset, spec §G. DOM-free and injectable; useKiosk
 * feeds it the visitor's input and tests drive it with fake timers.
 *
 *   state      watching ──(idleMs without input, phase explore)──▶ attract ──(touch, key, wheel)──▶ watching
 *
 *   attract    phase → "attract" (first, so the deselect is not announced and the sheet keeps its
 *              snap), bus "idle:attract", then the §G loop (runAttract) from a soft clear.
 *   wake       the input is consumed (useKiosk stops it reaching the map or the UI); choreo.cancel()
 *              (the current bloom or selection settles and stays as a starting point), captions
 *              clear, phase → "explore", bus "idle:wake".
 *   reset      resetMs without input: at the loop's next scene boundary resetAll() (+ phase back to
 *              "attract", which resetAll leaves at "explore"), bus "idle:reset", the loop restarts at
 *              scene 0 (camera home). Once per idle period.
 *   reload     every 6 h at a scene-0 boundary (optional; only when a reload function is given).
 *
 * The idle clock waits for phase "explore": it never starts attract over the intro, the loader or a
 * drive; if the visitor walked away mid-drive, attract starts as soon as the drive hands back.
 * A phase change made by someone else during attract (e.g. a deep link) ends attract like a wake.
 *
 * One timer at a time, re-armed lazily: an input only moves `lastInput`; the timer re-checks when it
 * fires, so a stream of pointermoves costs nothing.
 */
import type { BusEvents } from "@/contracts/bus";
import type { Choreographer } from "@/contracts/map";
import type { AppActions, AppState, Phase } from "@/contracts/store";
import { runAttract, type BusOn } from "./attract";
import { ATTRACT, type Caption } from "./script";

export type KioskState = "off" | "watching" | "attract";
export type KioskInput = "pointerdown" | "pointermove" | "touchstart" | "wheel" | "keydown";

type KioskStoreView = Pick<AppState, "phase" | "data" | "selectedId" | "bloomCategory" | "query">;

export interface KioskDeps {
  getState: () => KioskStoreView;
  subscribe: (fn: (s: KioskStoreView, p: KioskStoreView) => void) => () => void;
  actions: Pick<AppActions, "select" | "bloom" | "setCategory" | "setMood" | "setQuery" | "setPhase" | "resetAll">;
  choreo: () => Choreographer | null;
  on: BusOn;
  emit: (type: keyof Pick<BusEvents, "idle:attract" | "idle:wake" | "idle:reset">) => void;
  caption: (c: Caption | null, scene: number, loop: number) => void;
  now?: () => number;
  /** Page reload for the 6-hourly refresh; omitted = never reload. */
  reload?: () => void;
  /** Test seam for the loop's waits. */
  wait?: (ms: number, signal: AbortSignal) => Promise<void>;
}

export interface KioskTimes {
  idleMs?: number;
  resetMs?: number;
  reloadAfterMs?: number;
}

type Timer = ReturnType<typeof setTimeout>;

export class KioskController {
  private st: KioskState = "off";
  private lastInput = 0;
  private startedAt = 0;
  private timer: Timer | undefined;
  private loop: AbortController | null = null;
  private resetDone = false;
  private writing = false;
  private unsub: (() => void) | null = null;
  readonly idleMs: number;
  readonly resetMs: number;
  readonly reloadAfterMs: number;

  constructor(
    private readonly d: KioskDeps,
    t: KioskTimes = {},
  ) {
    this.idleMs = t.idleMs ?? ATTRACT.idleMs;
    this.resetMs = Math.max(t.resetMs ?? ATTRACT.resetMs, this.idleMs);
    this.reloadAfterMs = t.reloadAfterMs ?? ATTRACT.reloadAfterMs;
  }

  get state(): KioskState {
    return this.st;
  }
  /** ms since the last input (for the harness readout). */
  idleFor(): number {
    return this.st === "off" ? 0 : this.now() - this.lastInput;
  }

  enable(): void {
    if (this.st !== "off") return;
    this.st = "watching";
    this.startedAt = this.lastInput = this.now();
    this.resetDone = false;
    this.unsub = this.d.subscribe((s, p) => {
      if (s.phase !== p.phase) this.onPhase(s.phase);
    });
    this.schedule();
  }

  disable(): void {
    if (this.st === "off") return;
    if (this.st === "attract") this.leaveAttract({ writePhase: true });
    this.clearTimer();
    this.unsub?.();
    this.unsub = null;
    this.st = "off";
  }

  /** Visitor input. true: it woke the kiosk and must be consumed (not reach the map or the UI). */
  input(kind: KioskInput): boolean {
    if (this.st === "off") return false;
    // §G wakes on a touch, a key or the wheel. A pointer move (a mouse on its way to a click) never wakes
    // attract, so the click that follows is the one consumed; while watching it counts as activity.
    if (this.st === "attract" && kind === "pointermove") return false;
    this.lastInput = this.now();
    this.resetDone = false;
    if (this.st === "attract") {
      this.leaveAttract({ writePhase: true });
      this.schedule();
      return true;
    }
    if (this.timer === undefined) this.schedule();
    return false;
  }

  /** QA / harness: start attract now (as if idle). */
  forceAttract(): void {
    if (this.st === "watching") this.enterAttract();
  }
  /** QA / harness: make the reset due now (it runs at the next scene boundary). */
  forceResetDue(): void {
    if (this.st === "off") return;
    this.lastInput = Math.min(this.lastInput, this.now() - this.resetMs);
    this.resetDone = false;
    this.schedule();
  }

  /* ── internals ─────────────────────────────────────────────────── */

  private now(): number {
    return (this.d.now ?? Date.now)();
  }

  private clearTimer(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  /** One timer, only while watching in explore, at lastInput + idleMs. Attract keeps none (the loop checks
   *  the reset at its scene boundaries); other phases wait for onPhase("explore"). */
  private schedule(): void {
    this.clearTimer();
    if (this.st !== "watching" || this.d.getState().phase !== "explore") return;
    this.timer = setTimeout(() => this.tick(), Math.max(0, this.lastInput + this.idleMs - this.now()));
  }

  private tick(): void {
    this.timer = undefined;
    if (this.st !== "watching" || this.d.getState().phase !== "explore") return;
    if (this.now() - this.lastInput >= this.idleMs) this.enterAttract();
    else this.schedule();
  }

  private onPhase(phase: Phase): void {
    if (this.writing || this.st === "off") return;
    if (this.st === "attract" && phase !== "attract") {
      // Someone else took the app out of attract: stop the loop, leave the phase as they set it.
      this.leaveAttract({ writePhase: false });
      this.lastInput = this.now();
      this.schedule();
      return;
    }
    // Idle ran out while the intro or a drive was on: attract as soon as the app is back in explore.
    if (this.st === "watching" && phase === "explore") this.tick();
  }

  private write(fn: () => void): void {
    this.writing = true;
    try {
      fn();
    } finally {
      this.writing = false;
    }
  }

  private enterAttract(): void {
    if (this.st !== "watching") return;
    this.st = "attract";
    this.clearTimer();
    this.write(() => this.d.actions.setPhase("attract"));
    this.d.emit("idle:attract");
    const ac = (this.loop = new AbortController());
    void runAttract(
      {
        getState: this.d.getState,
        actions: this.d.actions,
        choreo: this.d.choreo,
        on: this.d.on,
        caption: this.d.caption,
        wait: this.d.wait,
        resetIfDue: () => {
          if (this.resetDone || this.now() - this.lastInput < this.resetMs) return false;
          this.reset();
          return true;
        },
        reloadIfDue: () => {
          if (!this.d.reload || this.now() - this.startedAt < this.reloadAfterMs) return false;
          this.d.reload();
          return true;
        },
      },
      ac.signal,
    ).catch((err: unknown) => console.error("[kiosk] attract loop", err));
  }

  private leaveAttract(o: { writePhase: boolean }): void {
    if (this.st !== "attract") return;
    this.st = "watching";
    this.loop?.abort();
    this.loop = null;
    try {
      this.d.choreo()?.cancel(); // the current bloom or selection settles and stays
    } catch (err) {
      console.error("[kiosk] cancel", err);
    }
    this.d.caption(null, -1, 0);
    if (o.writePhase && this.d.getState().phase === "attract") this.write(() => this.d.actions.setPhase("explore"));
    this.d.emit("idle:wake");
  }

  /** At a scene boundary of the loop, once per idle period. The loop's scene 0 then brings the camera home. */
  private reset(): void {
    this.resetDone = true;
    this.write(() => {
      this.d.actions.resetAll(); // group A, day, default layers, sort by time, deselect, no bloom; phase → explore
      this.d.actions.setPhase("attract");
    });
    this.d.emit("idle:reset");
  }
}
